import React, { useState } from "react";
import { ApolloClient, ApolloProvider, createHttpLink, from, InMemoryCache } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import AppContainer from "./AppContainer";
import "./App.css";
import ApolloLogin from "./Components/ApolloLogin";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import { ErrorBoundary } from "react-error-boundary";
import FallbackUI from "./Utils/FallbackUI";
import { DEMO_MODE_KEY, ResetDatabase } from "./Utils/TicketDatabase";
import { ApolloErrorParser, EXPIRED_TOKEN } from "./Utils/ApolloErrorParser";
import dayjs from "dayjs";
import ApolloLinkTimeout from "apollo-link-timeout";

import customParseFormat from "dayjs/plugin/customParseFormat";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import utc from "dayjs/plugin/utc";
import { logSentryError } from "./Utils/Sentry";
import { Font } from "@react-pdf/renderer";
import regularFont from "./Assets/Roboto-Regular.ttf";
import boldFont from "./Assets/Roboto-Bold.ttf";
import { isDevelopmentEnvironment } from "./Utils/UtilityMethods";
import { demoModeTheme, liveModeTheme } from "./MuiThemes";

import { IsDemoMode } from "./sharedCode/HelperMethods";
import InstallationPrompter from "./Components/InstallationPrompter";

dayjs.extend(customParseFormat);
dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(utc);

// have to register these fonts early
Font.register({
    family: "Roboto",
    fonts: [
        { src: regularFont, fontWeight: 400 },
        { src: boldFont, fontWeight: 700 },
    ],
});

export const AUTHORIZATION_TOKEN_STORAGE_KEY = "authorizationToken";
export const IDENTITY_BREADCRUMB = "identityBreadcrumb";

function App(): JSX.Element {
    const [authorizationToken, setAuthorizationToken] = useState(localStorage.getItem(AUTHORIZATION_TOKEN_STORAGE_KEY));
    const [loginMessage, setLoginMessage] = useState("");

    const [isDemoMode, setIsDemoMode] = useState(IsDemoMode());
    const [uiTheme, setUiTheme] = useState(liveModeTheme);

    const handleSetAuthorizationToken = (token: string | null) => {
        localStorage.setItem(AUTHORIZATION_TOKEN_STORAGE_KEY, token ?? "");
        setAuthorizationToken(token);
        setLoginMessage("");
        window.location.reload(); // have to re-render the app for the gql queries to use the new token
    };

    const handleToggleDemoMode = () => {
        setIsDemoMode((prev) => {
            const newMode = !prev;
            localStorage.setItem(DEMO_MODE_KEY, newMode ? "Y" : "N");
            setUiTheme(newMode ? demoModeTheme : liveModeTheme);
            return newMode;
        });
    };

    const timeoutLink = new ApolloLinkTimeout(3 * 60 * 1000);

    const httpLink = createHttpLink({
        uri: process.env.REACT_APP_GRAPHQL_SERVER,
    });

    const graphqlServerHttp = timeoutLink.concat(httpLink);

    const errorLink = onError(({ graphQLErrors, networkError }) => {
        if (graphQLErrors)
            graphQLErrors.forEach((gqlError) => {
                if (ApolloErrorParser.parse(gqlError.message) === EXPIRED_TOKEN && authorizationToken) {
                    // force a re-login
                    localStorage.setItem(AUTHORIZATION_TOKEN_STORAGE_KEY, "");
                    setAuthorizationToken("");
                    setLoginMessage("Your previous login session has expired, you will need to re-login to continue.");
                } else {
                    logSentryError(gqlError);
                }
            });
    });

    const getClientCode = () => {
        // use a generic client code entry in development
        if (isDevelopmentEnvironment()) {
            return process.env["REACT_APP_DEVELOPMENT_CLIENT_CODE"];
        }

        //TODO need some error handling here, if we can't find a client code
        const clientCode = process.env["REACT_APP_" + window.location.hostname];

        if (!clientCode) {
            logSentryError(new Error(`unable to lookup client code for ${window.location.hostname} : ${JSON.stringify(process.env)}`));
        }

        return clientCode ?? "UNKNOWN_CLIENT_" + window.location.hostname;
    };

    const apolloLink = setContext((_, { headers }) => {
        // return the headers to the context so httpLink can read them
        const newHeaders = {
            headers: {
                ...headers,
                authorization: authorizationToken ? `Bearer ${authorizationToken}` : "",
                xavroclientid: getClientCode(),
                hostname: window.location.hostname,
            },
        };
        return newHeaders;
    });

    const [apolloClient, setApolloClient] = useState(
        new ApolloClient({
            link: from([errorLink, apolloLink.concat(graphqlServerHttp)]),
            cache: new InMemoryCache(),
        })
    );

    React.useEffect(() => {
        const setClient = async () => {
            const apolloLink = setContext((_, { headers }) => {
                // return the headers to the context so httpLink can read them
                const newHeaders = {
                    headers: {
                        ...headers,
                        authorization: authorizationToken ? `Bearer ${authorizationToken}` : "",
                        xavroclientid: getClientCode(),
                        hostname: window.location.hostname,
                    },
                };
                return newHeaders;
            });

            setApolloClient(
                new ApolloClient({
                    link: from([errorLink, apolloLink.concat(graphqlServerHttp)]),
                    cache: new InMemoryCache(),
                })
            );
        };

        setClient();
    }, [authorizationToken]);

    React.useEffect(() => {
        setUiTheme(isDemoMode ? demoModeTheme : liveModeTheme);
    }, [isDemoMode]);

    const doErrorReset = () => {
        ResetDatabase();
    };

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const fromHomeScreen = window.matchMedia("(display-mode: standalone)").matches;

    // TODO this could use some work, if we're not in the homepage but not running in safari we let the app run - lets us use chrome in dev
    const redirectToInstallRequired = !fromHomeScreen && isSafari;

    if (redirectToInstallRequired)
        return (
            <ThemeProvider theme={uiTheme}>
                <InstallationPrompter />
            </ThemeProvider>
        );

    return (
        <ThemeProvider theme={uiTheme}>
            <CssBaseline>
                <ApolloProvider client={apolloClient}>
                    <ErrorBoundary FallbackComponent={FallbackUI} onReset={doErrorReset}>
                        {authorizationToken && <AppContainer setAuthorizationToken={handleSetAuthorizationToken} toggleDemoMode={handleToggleDemoMode} />}
                        {!authorizationToken && <ApolloLogin setAuthorizationToken={handleSetAuthorizationToken} loginMessage={loginMessage} />}
                    </ErrorBoundary>
                </ApolloProvider>
            </CssBaseline>
        </ThemeProvider>
    );
}

export default App;
